import React, { useCallback, useRef } from "react";
import { Typography } from "@mzc-pdc/ui";
import Tooltip from "@components_v2/Tooltip/tooltip";
import useTextOverflow from "@hooks/useTextOverflow";

// MUI 의 기본 variantMapping 중 블록 요소로 렌더되는 variant. 여기 없으면 span(=inline) 이 된다.
const MUI_BLOCK_VARIANTS = new Set([
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "subtitle1",
    "subtitle2",
    "body1",
    "body2",
    "inherit",
]);
const BLOCK_TAGS = new Set(["h1", "h2", "h3", "h4", "h5", "h6", "p", "div", "li", "section", "article"]);

/**
 * 말줄임(…)이 실제로 발생했을 때만 툴팁으로 전체 텍스트를 노출하는 Typography.
 *
 * Typography 의 props 를 그대로 전달하며 MUI Tooltip 은 DOM 노드를 추가하지 않으므로
 * 기존 마크업·스타일을 그대로 유지한 채 툴팁만 덧붙는다. (DAM-306)
 *
 * @param {React.ReactNode} tooltipTitle 툴팁에 표시할 내용. 없으면 children 을 사용한다.
 * @param {number} lineClamp 2 이상이면 해당 줄 수까지 줄바꿈한 뒤 말줄임 처리한다.
 */
const EllipsisTypography = React.forwardRef(
    ({ children, tooltipTitle, tooltipPlacement = "bottom", lineClamp, sx, ...rest }, forwardedRef) => {
        const elementRef = useRef(null);
        const isOverflow = useTextOverflow(elementRef, [children, lineClamp]);

        const handleRef = useCallback(
            (node) => {
                elementRef.current = node;
                if (typeof forwardedRef === "function") forwardedRef(node);
                else if (forwardedRef) forwardedRef.current = node;
            },
            [forwardedRef],
        );

        // 한 줄 말줄임을 요청했는지 — noWrap prop 또는 호출부 sx 의 줄바꿈 금지 선언으로 판단한다.
        // whiteSpace 와 textWrap 둘 다 쓰이고 있어 함께 본다.
        const isSingleLineClipped =
            rest.noWrap === true || sx?.whiteSpace === "nowrap" || sx?.textWrap === "nowrap";

        // 이 Typography 가 인라인으로 렌더되는지. 이미 블록인 곳(component="p"·variant="body1" 등)은
        // 건드리지 않는다 — inline-block 으로 바꾸면 줄바꿈이 사라져 뒤 형제가 같은 줄로 올라온다.
        // 태그는 component > paragraph > variantMapping 순으로 정해진다(MUI Typography.js:114).
        // 기본 variant 는 body1 이고 그건 <p> 로 렌더된다(Typography.js:99).
        // variant 를 안 준 호출부를 span 으로 보면 블록 요소를 inline-block 으로 바꿔 버린다.
        // 문자열이 아닌 component 는 무엇으로 렌더될지 알 수 없으므로 건드리지 않는다.
        const rendersInline =
            typeof rest.component === "string"
                ? !BLOCK_TAGS.has(rest.component)
                : rest.component
                ? false
                : !rest.paragraph && !MUI_BLOCK_VARIANTS.has(rest.variant ?? "body1");

        const clampSx =
            lineClamp > 1
                ? {
                      display: "-webkit-box",
                      WebkitBoxOrient: "vertical",
                      WebkitLineClamp: lineClamp,
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      overflow: "hidden",
                  }
                : isSingleLineClipped && rendersInline
                ? {
                      // 잘림 감지는 scrollWidth > clientWidth 로 하는데 inline 박스는 둘 다 0 이라 영영 false 다.
                      // MUI 는 variantMapping 에 없는 variant(이 저장소의 body1_500 등)를 span 으로 폴백하므로
                      // noWrap 만 준 호출부는 대부분 inline 이 되어 툴팁이 뜨지 않았다. (DAM-654)
                      // 인라인 흐름은 유지하면서 측정 가능한 박스를 만들기 위해 inline-block 으로 올린다.
                      display: "inline-block",
                      maxWidth: "100%",
                      // overflow:hidden 인 inline-block 은 아래 margin edge 가 baseline 이 되어 글자가 떠 보인다.
                      verticalAlign: "bottom",
                  }
                : null;

        return (
            <Tooltip title={isOverflow ? tooltipTitle ?? children : ""} placement={tooltipPlacement}>
                <Typography ref={handleRef} sx={{ ...clampSx, ...sx }} {...rest}>
                    {children}
                </Typography>
            </Tooltip>
        );
    },
);

EllipsisTypography.displayName = "EllipsisTypography";

export default EllipsisTypography;
